import { notify, Progress, SidebarWithoutTransition, useLayout } from '@eleveo/components-react';
import { AxiosError } from 'axios';
import { FORM_ERROR, ValidationErrors } from 'final-form';
import arrayMutators from 'final-form-arrays';
import { equals } from 'ramda';
import React, { useCallback, useMemo } from 'react';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { UseMutateFunction } from 'react-query';

import { cleanUpIntegrationData, formatIntegrationValues } from './config';
import { ConfigurationType } from './types';
import {
  useCreateConfigurationQuery,
  useConfigurationByIdQuery,
  useUpdateConfigurationQuery,
} from '../../api/integrationsQuery';
import { ETLErrorResponse } from '../../api/types';
import { useConfig } from '../../app/ConfigurationProvider';
import { useIntegrations } from '../../app/IntegrationsProvider';
import { ETL_INTEGRATION_TYPE, INTEGRATIONS_WITH_ROLE_MAPPING } from '../../components/configuration/types';
import { MSTeamsForm } from '../../components/forms/msteams/MSTeamsForm';
import { WebexCCForm } from '../../components/forms/webex/WebexCCForm';
import { WebexCallingForm } from '../../components/forms/webex/WebexCallingForm';
import { ZoomCCForm } from '../../components/forms/zoom/ZoomCCForm';
import { useSearchNavigate } from '../../hooks/useSearchNavigate';
import { validateMSTeamsForm } from '../../utils/validations/msTeamsValidator';
import { validateWXCallingForm } from '../../utils/validations/webexCallingValidator';
import { validateWXCCForm } from '../../utils/validations/webexccValidator';
import { validateZoomCCForm } from '../../utils/validations/zoomccValidator';

export const IntegrationSideBarFormWrapper: React.FC = () => {
  // Initialize in the default state, even though the previous Sidebar could have been expanded.
  useLayout({
    expanded: false,
  });

  const { integrationId, isInEditMode, integrationType } = useIntegrations();
  const { webexccApiUrl, msteamsApiUrl, zoomccApiUrl, webexCallingApiUrl } = useConfig();

  const { navigateToMyConfigurations } = useSearchNavigate();
  const { t } = useTranslation();

  const apiUrl = useMemo(() => {
    switch (integrationType) {
      case ETL_INTEGRATION_TYPE.wxcc:
        return webexccApiUrl;
      case ETL_INTEGRATION_TYPE.msteams:
        return msteamsApiUrl;
      case ETL_INTEGRATION_TYPE.zoomcc:
        return zoomccApiUrl;
      case ETL_INTEGRATION_TYPE.wxcalling:
        return webexCallingApiUrl;
      default:
        throw new Error('Invalid integration type!');
    }
  }, [webexccApiUrl, msteamsApiUrl, zoomccApiUrl, webexCallingApiUrl, integrationType]);

  const handleSideBarClose = (resolve?: () => void) => {
    navigateToMyConfigurations();
    resolve?.();
  };

  const { mutate: createIntegration, isLoading: isCreateInProgress } = useCreateConfigurationQuery();
  const { mutate: updateIntegration, isLoading: isUpdateInProgress } = useUpdateConfigurationQuery();
  const { data: integrationById, isLoading } = useConfigurationByIdQuery(integrationId, {
    onError: () => handleSideBarClose(),
  });

  /**
   * Form itself should handle correct data structure, submit will
   * only resend data received from form to `/configuration` endpoint.
   */
  const handleMutation = useCallback(
    async (
      values: ConfigurationType,
      mutation: UseMutateFunction<ConfigurationType, AxiosError<ETLErrorResponse>, ConfigurationType, unknown>
    ) =>
      new Promise<void | { [FORM_ERROR]: 'mutation error' }>((resolve) =>
        mutation(values, {
          onSuccess: () => {
            notify.success(t('myConfigurations.response.success'));
            handleSideBarClose(resolve);
          },
          onError: () => resolve({ [FORM_ERROR]: 'mutation error' }),
        })
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleSideBarClose]
  );

  const handleSubmit = useCallback(
    async (values: ConfigurationType) =>
      handleMutation(cleanUpIntegrationData(values), isInEditMode ? updateIntegration : createIntegration),
    [isInEditMode, createIntegration, updateIntegration, handleMutation]
  );

  const formInitialValues: ConfigurationType = useMemo(
    () => formatIntegrationValues(isInEditMode && integrationById ? integrationById : null, integrationType, apiUrl),
    [apiUrl, integrationType, integrationById, isInEditMode]
  );

  const isSideBarDisplayed = !isInEditMode || !!integrationById;

  const FormToRender = useMemo(() => {
    switch (integrationType.toLowerCase()) {
      case ETL_INTEGRATION_TYPE.wxcc:
        return WebexCCForm;
      case ETL_INTEGRATION_TYPE.zoomcc:
        return ZoomCCForm;
      case ETL_INTEGRATION_TYPE.msteams:
        return MSTeamsForm;
      case ETL_INTEGRATION_TYPE.wxcalling:
        return WebexCallingForm;
      default:
        throw new Error('[IntegrationSideBarFormWrapper] FormToRender: invalid integrationType!');
    }
  }, [integrationType]);

  const validatorToUse = useMemo(() => {
    switch (integrationType.toLowerCase()) {
      case ETL_INTEGRATION_TYPE.wxcc:
        return validateWXCCForm;
      case ETL_INTEGRATION_TYPE.zoomcc:
        return validateZoomCCForm;
      case ETL_INTEGRATION_TYPE.msteams:
        return validateMSTeamsForm;
      case ETL_INTEGRATION_TYPE.wxcalling:
        return validateWXCallingForm;
      default:
        throw new Error('[IntegrationSideBarFormWrapper] validatorToUse: invalid integrationType!');
    }
  }, [integrationType]);

  const title = t(`myConfigurations.${isInEditMode ? 'edit' : 'create'}`);

  // Integrations without role mapping in the expanded sidebar.
  const isExpandable = (INTEGRATIONS_WITH_ROLE_MAPPING as readonly string[]).includes(integrationType.toLowerCase());

  return (
    <>
      {(isLoading || isCreateInProgress || isUpdateInProgress) && <Progress withOverlay />}
      {isSideBarDisplayed && (
        <Form<ConfigurationType>
          // force rerender when integration in edit mode changes because of `keepDirtyOnReinitialize`
          key={isInEditMode ? integrationById?.id : undefined}
          initialValues={formInitialValues}
          initialValuesEqual={equals}
          onSubmit={handleSubmit}
          keepDirtyOnReinitialize
          validate={validatorToUse as (values: ConfigurationType) => ValidationErrors}
          mutators={{
            ...arrayMutators,
          }}
        >
          {(renderProps) => (
            <SidebarWithoutTransition isExpandable={isExpandable} onClose={() => handleSideBarClose()} title={title}>
              <form aria-label={title} className="contents" onSubmit={renderProps.handleSubmit}>
                <FormToRender />
              </form>
            </SidebarWithoutTransition>
          )}
        </Form>
      )}
    </>
  );
};
